import { Link } from 'react-router-dom';
import Button from '@mui/material/Button';
import SeoHelmet from '../../common/SeoHelmet';
import Header from '../../common/Header';
import Footer from '../../common/Footer';

const HansenTheory = () => {
  return (
    <>
      <SeoHelmet
        title="GeoCalc | Bearing Capacity of Shallow Foundations - Hansen (1970) | Theory"
        description="Theory and formulation of Hansen's (1970) bearing capacity method for shallow foundations, including shape, depth, inclination, and ground correction factors extending Terzaghi’s classical model."
        url="https://www.geocalc.app/hansen-theory"
        image="https://www.geocalc.app/assets/og-theory-v1.jpg"
        schemaType="page"
        breadcrumbs={[
          { name: 'Home', url: 'https://www.geocalc.app/' },
          { name: 'Hansen Bearing Capacity | Theory', url: 'https://www.geocalc.app/hansen-theory' },
        ]}
      />

      <Header />
      <div className="theory">
        <div className="theoryContainer">
          <h1>Bearing Capacity of Shallow Foundations - Hansen (1970 ) | Theory & Formulations</h1>

          <h2>(Based on "Principles of Foundation Engineering", 4th Edition by Braja M. Das)</h2>

          <p>
            Hansen (1970) extended Terzaghi&rsquo;s classical bearing capacity theory by introducing{' '}
            <strong>more realistic correction factors</strong> to account for various field conditions.
          </p>

          <h3>Ultimate Bearing Capacity Formula:</h3>
          <p>
            q<sub>u</sub> = c' N<sub>c</sub> s<sub>c</sub> d<sub>c</sub> i<sub>c</sub> g<sub>c</sub> b<sub>c</sub> + q N
            <sub>q</sub> s<sub>q</sub> d<sub>q</sub> i<sub>q</sub> g<sub>q</sub> b<sub>q</sub> + (1/2) &gamma; B N
            <sub>&gamma;</sub> s<sub>&gamma;</sub> d<sub>&gamma;</sub> i<sub>&gamma;</sub> g<sub>&gamma;</sub> b<sub>&gamma;</sub>
          </p>

          <h3>Where:</h3>
          <ul>
            <li>c' = effective cohesion</li>
            <li>
              q = overburden pressure at the footing base (q = &gamma; D<sub>f</sub>)
            </li>
            <li>&gamma; = unit weight of soil</li>
            <li>B = width of foundation</li>
            <li>
              N<sub>c</sub>, N<sub>q</sub>, N<sub>&gamma;</sub> = bearing capacity factors (functions of soil friction angle
              &phi;')
            </li>
          </ul>

          <h3>Correction Factors Introduced by Hansen:</h3>
          <ul>
            <li>
              <strong>Shape factors</strong> (s<sub>c</sub>, s<sub>q</sub>, s<sub>&gamma;</sub>)
            </li>
            <li>
              <strong>Depth factors</strong> (d<sub>c</sub>, d<sub>q</sub>, d<sub>&gamma;</sub>)
            </li>
            <li>
              <strong>Load inclination factors</strong> (i<sub>c</sub>, i<sub>q</sub>, i<sub>&gamma;</sub>)
            </li>
            <li>
              <strong>Ground inclination factors</strong> (g<sub>c</sub>, g<sub>q</sub>, g<sub>&gamma;</sub>)
            </li>
            <li>
              <strong>Base inclination factors</strong> (b<sub>c</sub>, b<sub>q</sub>, b<sub>&gamma;</sub>)
            </li>
          </ul>

          <p>
            These factors adjust the basic bearing capacity terms to account for:
            <br />
            - Footing shape (e.g., rectangular, square, circular)
            <br />
            - Depth of foundation
            <br />
            - Inclined loading
            <br />
            - Ground slope near the foundation
            <br />- Base slope of foundation
          </p>

          <h3>Notes:</h3>
          <ul>
            <li>
              For <strong>level ground</strong> and <strong>horizontal footing bases</strong> with <strong>vertical loads</strong>
              , all ground and base factors are typically taken as 1.
            </li>
            <li>
              Hansen&rsquo;s method is <strong>more general</strong> and <strong>more conservative</strong> compared to
              Terzaghi&rsquo;s original theory.
            </li>
            <li>
              Applicable to <strong>non-horizontal</strong> and <strong>rough footing bases</strong> unlike Terzaghi&rsquo;s
              method.
            </li>
          </ul>

          <h2>Key Point:</h2>
          <p>
            Hansen&rsquo;s 1970 method expanded the classical bearing capacity formula by adding more correction factors, making
            it more flexible and accurate for different practical site conditions.
          </p>
        </div>
        <div className="theoryButtonContainer">
          <Link to="/calculations/hansen">
            <Button variant="outlined" size="small">
              GO TO CALCULATOR
            </Button>
          </Link>
        </div>
      </div>
      <Footer />
    </>
  );
};

export default HansenTheory;
